const EVENT_TYPE_TO_NAMES = Object.freeze({
  session: ['session.start', 'session.end'],
  route: ['route.view'],
  ui: [
    'ui.click',
    'ui.input.change',
    'ui.select.change',
    'ui.toggle',
    'ui.tab.change',
    'ui.drawer.open',
    'ui.drawer.close',
    'ui.modal.open',
    'ui.modal.close',
  ],
  form: ['form.submit'],
  download: ['download.start'],
  export: ['export.start', 'export.complete'],
  api: ['api.mutation.success', 'api.mutation.error'],
});

export const ACTIVITY_EVENT_TYPES = Object.freeze(Object.keys(EVENT_TYPE_TO_NAMES));
export const ACTIVITY_EVENT_NAMES = Object.freeze(
  ACTIVITY_EVENT_TYPES.flatMap((eventType) => EVENT_TYPE_TO_NAMES[eventType]),
);
export const ACTIVITY_EVENT_NAMES_BY_TYPE = EVENT_TYPE_TO_NAMES;
export const ACTIVITY_EVENT_NAME_TO_TYPE = Object.freeze(
  ACTIVITY_EVENT_NAMES.reduce((acc, eventName) => {
    const eventType = ACTIVITY_EVENT_TYPES.find((candidate) =>
      EVENT_TYPE_TO_NAMES[candidate].includes(eventName),
    );
    acc[eventName] = eventType;
    return acc;
  }, {}),
);

export const ACTIVITY_REPORT_STATUSES = Object.freeze(['queued', 'running', 'completed', 'error']);

export const ACTIVITY_REPORT_REQUEST_VIAS = Object.freeze(['app', 'cli']);

// Plain-language labels for the activity taxonomy so investigator-facing filters
// never surface raw enum tokens (per the PRIME plain-language copy rule). The
// underlying value stays the canonical token; only the display text is friendly.
export const ACTIVITY_EVENT_TYPE_LABELS = Object.freeze({
  session: 'Session',
  route: 'Page view',
  ui: 'UI interaction',
  form: 'Form submission',
  download: 'Download',
  export: 'Export',
  api: 'Data change',
});

export const ACTIVITY_EVENT_NAME_LABELS = Object.freeze({
  'session.start': 'Session started',
  'session.end': 'Session ended',
  'route.view': 'Viewed a page',
  'ui.click': 'Clicked a control',
  'ui.input.change': 'Edited a text field',
  'ui.select.change': 'Changed a dropdown',
  'ui.toggle': 'Toggled a switch',
  'ui.tab.change': 'Switched tabs',
  'ui.drawer.open': 'Opened a drawer',
  'ui.drawer.close': 'Closed a drawer',
  'ui.modal.open': 'Opened a dialog',
  'ui.modal.close': 'Closed a dialog',
  'form.submit': 'Submitted a form',
  'download.start': 'Started a download',
  'export.start': 'Started an export',
  'export.complete': 'Completed an export',
  'api.mutation.success': 'Saved a change',
  'api.mutation.error': 'Failed to save a change',
});

const ACTIVITY_REPORT_STATUS_LABELS = Object.freeze({
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  error: 'Failed',
});

export function activityEventTypeLabel(eventType) {
  const key = String(eventType || '');
  return ACTIVITY_EVENT_TYPE_LABELS[key] || key;
}

export function activityEventNameLabel(eventName) {
  const key = String(eventName || '');
  return ACTIVITY_EVENT_NAME_LABELS[key] || key;
}

export function activityReportStatusLabel(status) {
  const key = String(status || '').toLowerCase();
  return (
    ACTIVITY_REPORT_STATUS_LABELS[key] ||
    (key ? `${key[0].toUpperCase()}${key.slice(1)}` : 'Unknown')
  );
}

export function activityReportCompletenessSummary(completeness) {
  if (!completeness?.events || !completeness?.sessions) return '';
  const formatCount = (value) => Number(value || 0).toLocaleString('en-US');
  const collectionSummary = (label, collection) =>
    `${label} included ${formatCount(collection.included)} of ${formatCount(collection.matched)} matched ` +
    `(limit ${formatCount(collection.limit)}; ${collection.truncated ? 'truncated' : 'complete'}).`;
  return [
    `Captured at ${completeness.capturedAt || 'an unavailable time'}.`,
    collectionSummary('Events', completeness.events),
    collectionSummary('Sessions', completeness.sessions),
    completeness.identityCoverage?.verified
      ? 'Identity coverage verified for every requested sandbox assignment.'
      : 'Identity coverage was not verified.',
    completeness.deliveryCoverage?.verified
      ? 'Delivery coverage verified from fresh sandbox heartbeats with no overlapping eligible backlog.'
      : 'Delivery coverage was not verified.',
    completeness.continuationGuidance || '',
  ]
    .filter(Boolean)
    .join(' ');
}

export function resolveExpectedActivityEventType(eventName) {
  return ACTIVITY_EVENT_NAME_TO_TYPE[String(eventName || '')] || null;
}

export function isValidActivityEventType(eventType) {
  return ACTIVITY_EVENT_TYPES.includes(String(eventType || ''));
}

export function isValidActivityEventName(eventName) {
  return ACTIVITY_EVENT_NAMES.includes(String(eventName || ''));
}

export function validateActivityEvent(event = {}) {
  const errors = [];
  if (!event.clientEventId) errors.push('clientEventId is required');
  if (!Number.isInteger(event.sequenceNo) || event.sequenceNo < 1) {
    errors.push('sequenceNo must be a positive integer');
  }
  if (!event.occurredAt || Number.isNaN(new Date(event.occurredAt).getTime())) {
    errors.push('occurredAt must be a valid ISO timestamp');
  }
  if (!isValidActivityEventType(event.eventType)) {
    errors.push(`eventType must be one of: ${ACTIVITY_EVENT_TYPES.join(', ')}`);
  }
  if (!isValidActivityEventName(event.eventName)) {
    errors.push(`eventName must be one of: ${ACTIVITY_EVENT_NAMES.join(', ')}`);
  }
  const expectedType = resolveExpectedActivityEventType(event.eventName);
  if (expectedType && String(event.eventType || '') !== expectedType) {
    errors.push(`eventName ${event.eventName} requires eventType ${expectedType}`);
  }
  return errors;
}

export function validateActivityBatch(batch = {}) {
  const errors = [];
  if (!batch?.session?.id) errors.push('session.id is required');
  if (!batch?.session?.startedAt || Number.isNaN(new Date(batch.session.startedAt).getTime())) {
    errors.push('session.startedAt must be a valid ISO timestamp');
  }
  if (!Array.isArray(batch?.events) || batch.events.length === 0) {
    errors.push('events must be a non-empty array');
  } else {
    batch.events.forEach((event, index) => {
      validateActivityEvent(event).forEach((message) => {
        errors.push(`events[${index}]: ${message}`);
      });
    });
  }
  return errors;
}
